import React from 'react';
import { TrackConfig, SortMode, ColumnConfig } from '../types';
import { ColumnSettings } from './ColumnSettings';
import './ControlPanel.css';
import { navigate } from './EventsList';

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'average',   label: 'Average Lap (5)' },
  { value: 'average10', label: 'Average Lap (10)' },
  { value: 'best',      label: 'Best Lap' },
  { value: 'position',  label: 'Position' },
  { value: 'category',  label: 'Category (grouped)' },
];

interface ControlPanelProps {
  selectedTrack: TrackConfig;
  onStart: () => void;
  onStop: () => void;
  onBackToTracks: () => void;
  onSortModeChange: (mode: SortMode) => void;
  sortMode: SortMode;
  onToggleLogs: () => void;
  isLogsVisible: boolean;
  onToggleRaceControl: () => void;
  isRaceControlVisible: boolean;
  onToggleStrategy: () => void;
  isStrategyVisible: boolean;
  onToggleCharts: () => void;
  isChartsVisible: boolean;
  onToggleSectors: () => void;
  isSectorsVisible: boolean;
  onTogglePlan: () => void;
  isPlanVisible: boolean;
  onToggleAlerts: () => void;
  isAlertsVisible: boolean;
  alertCount: number;
  /**
   * The stint plan is for whoever has a strategy, admin or not, so it is not
   * behind the admin flag like the rest of the race-engineering panels.
   */
  onToggleStintPlan: () => void;
  isStintPlanVisible: boolean;
  /** How many strategies are attached to the race being recorded. */
  stintPlanCount: number;
  /** Alerts are admin-only, except for the planned-stop prompts of a plan you can see. */
  canSeeAlerts: boolean;
  raceControlCount: number;
  isSocketConnected: boolean;
  isApexConnected: boolean;
  columns: ColumnConfig[];
  onToggleColumn: (key: string) => void;
  onResetColumns: () => void;
  onShowAllColumns: () => void;
  onHideOptionalColumns: () => void;
  /** Admin-only extras that stay put, such as the log panel toggle. */
  isAdmin: boolean;
  /** The Start/Stop bar: admin-only *and* subject to the header toggle. */
  showConnectionControls: boolean;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  selectedTrack,
  onStart,
  onStop,
  onBackToTracks,
  onSortModeChange,
  sortMode,
  onToggleLogs,
  isLogsVisible,
  onToggleRaceControl,
  isRaceControlVisible,
  onToggleStrategy,
  isStrategyVisible,
  onToggleCharts,
  isChartsVisible,
  onToggleSectors,
  isSectorsVisible,
  onTogglePlan,
  isPlanVisible,
  onToggleAlerts,
  isAlertsVisible,
  alertCount,
  onToggleStintPlan,
  isStintPlanVisible,
  stintPlanCount,
  canSeeAlerts,
  raceControlCount,
  isSocketConnected,
  isApexConnected,
  columns,
  onToggleColumn,
  onResetColumns,
  onShowAllColumns,
  onHideOptionalColumns,
  isAdmin,
  showConnectionControls,
  searchQuery,
  onSearchQueryChange,
}) => {
  return (
    <div className="control-panel">
      <div className="control-group">
        <div className="track-info">
          <h3>{selectedTrack.name}</h3>
          <p>Host: {selectedTrack.host}:{selectedTrack.wsPort}</p>
          {showConnectionControls && (
            <>
              <p className={`connection-status ${isApexConnected ? 'connected' : 'disconnected'}`}>
                Apex: {isApexConnected ? 'Connected' : 'Disconnected'}
              </p>
              <p className={`connection-status ${isSocketConnected ? 'connected' : 'disconnected'}`}>
                Backend: {isSocketConnected ? 'Connected' : 'Disconnected'}
              </p>
            </>
          )}
        </div>

        {showConnectionControls && (
          <div className="connection-buttons">
            <button
              className="control-button start-button"
              onClick={onStart}
              disabled={!isSocketConnected || isApexConnected}
              title={!isSocketConnected ? 'Backend not connected' : isApexConnected ? 'Already listening' : 'Start listening to Apex'}
            >
              Start
            </button>

            <button
              className="control-button stop-button"
              onClick={onStop}
              disabled={!isApexConnected}
              title={!isApexConnected ? 'Not currently listening' : 'Stop listening to Apex'}
            >
              Stop
            </button>

            <button
              className="control-button disconnect-button"
              onClick={onBackToTracks}
            >
              Change Track
            </button>
          </div>
        )}
      </div>

      <div className="control-group">
        <div className="search-wrapper">
          <label className="sort-label" htmlFor="team-search">Search</label>
          <input
            id="team-search"
            type="search"
            className="team-search-input"
            placeholder="Driver, kart, team, category…"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onSearchQueryChange('');
            }}
            autoComplete="off"
            spellCheck={false}
          />
          {searchQuery && (
            <button
              type="button"
              className="team-search-clear"
              onClick={() => onSearchQueryChange('')}
              title="Clear search (Esc)"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="sort-dropdown-wrapper">
          <label className="sort-label" htmlFor="sort-select">Sort</label>
          <select
            id="sort-select"
            className="sort-select"
            value={sortMode}
            onChange={(e) => onSortModeChange(e.target.value as SortMode)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <button
          className="control-button race-control-button"
          onClick={onToggleRaceControl}
        >
          {isRaceControlVisible ? 'Hide' : 'Show'} Race Control
          {raceControlCount > 0 && (
            <span className="race-control-badge">{raceControlCount}</span>
          )}
        </button>

        {isAdmin && (
          <button
            className="control-button strategy-button"
            onClick={onToggleStrategy}
            aria-pressed={isStrategyVisible}
          >
            {isStrategyVisible ? 'Hide' : 'Show'} Strategy
          </button>
        )}

        {isAdmin && (
          <button
            className="control-button charts-button"
            onClick={onToggleCharts}
            aria-pressed={isChartsVisible}
          >
            {isChartsVisible ? 'Hide' : 'Show'} Charts
          </button>
        )}

        {isAdmin && (
          <button
            className="control-button sectors-button"
            onClick={onToggleSectors}
            aria-pressed={isSectorsVisible}
          >
            {isSectorsVisible ? 'Hide' : 'Show'} Sectors
          </button>
        )}

        {isAdmin && (
          <button
            className="control-button plan-button"
            onClick={onTogglePlan}
            aria-pressed={isPlanVisible}
          >
            {isPlanVisible ? 'Hide' : 'Show'} Driver Plan
          </button>
        )}

        <button
          className="control-button plan-button"
          onClick={onToggleStintPlan}
          aria-pressed={isStintPlanVisible}
          title="Follow a team's stint plan: the next planned stop and what it changes"
        >
          {isStintPlanVisible ? 'Hide' : 'Show'} Stint Plan
          {stintPlanCount > 0 && <span className="race-control-badge">{stintPlanCount}</span>}
        </button>

        {canSeeAlerts && (
          <button
            className="control-button alerts-button"
            onClick={onToggleAlerts}
            aria-pressed={isAlertsVisible}
          >
            {isAlertsVisible ? 'Hide' : 'Show'} Alerts
            {alertCount > 0 && <span className="race-control-badge">{alertCount}</span>}
          </button>
        )}

        {isAdmin && (
          <button
            className="control-button logs-button"
            onClick={onToggleLogs}
          >
            {isLogsVisible ? 'Hide' : 'Show'} Logs
          </button>
        )}

        <ColumnSettings
          columns={columns}
          onToggleColumn={onToggleColumn}
          onResetColumns={onResetColumns}
          onShowAll={onShowAllColumns}
          onHideOptional={onHideOptionalColumns}
        />

        <button
          className="control-button pit-button"
          onClick={() => navigate('/pit')}
          title="Open the pit lane monitor on its own screen"
        >
          Pit Monitor →
        </button>

        <button
          className="control-button pit-button"
          onClick={() => navigate('/strategy')}
          title="Plan stints and driver changes against the regulations"
        >
          Strategy →
        </button>

        <button
          className="control-button pit-button"
          onClick={() => navigate('/rankings')}
          title="All-time driver rankings across recorded events"
        >
          Rankings →
        </button>
      </div>
    </div>
  );
};
